import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Button, IconButton, Grid, Paper } from "@mui/material";
import RestoreIcon from '@mui/icons-material/Restore';
import HomeIcon from '@mui/icons-material/Home';
import TimerIcon from '@mui/icons-material/Timer';

const sanctionsLabels = ["", "MISE EN GARDE", "AVERTISSEMENT", "DISQUALIFICATION"];

/**
 * BoxeMatch
 *
 * Props:
 * - params: object contenant les settings et les participants :
 *    {
 *      durationMin, durationSec, type, valeurPoing, valeurPied, nbReprises,
 *      classeId (optional),
 *      blueId/redId/arbitreId (optional)  OR  nomBlue/nomRed (manual mode),
 *      activity (optional)
 *    }
 * - onFinish(matchResult) : appelé quand le match est terminé (ou disqualification)
 * - onHome(), onReset() callbacks
 * - qrMode (unused here)
 *
 * Comportement :
 * - gère le timer, le scoring par reprise, sanctions
 * - lorsque le match est terminé, appelle onFinish avec un objet complet :
 *   {
 *     params: <params passed in>,
 *     rounds: [...],
 *     participants,
 *     createdAt: ISOString
 *   }
 */

export default function BoxeMatch({ params = {}, onFinish, onHome, onReset, qrMode }) {
  const nbReprises = params.nbReprises || params.nbReprises === 0 ? params.nbReprises : (params.nbReprises || params.nbReprises === 0 ? params.nbReprises : (params.nbReprises ?? params.rounds ?? 1));
  const roundsCount = Number(nbReprises) || 1;
  const initialTime = Number(params.durationMin || params.duration?.min || params.durationMin || 1) * 60 + Number(params.durationSec || params.duration?.sec || params.durationSec || 0);

  const [currentRound, setCurrentRound] = useState(1);
  const [time, setTime] = useState(initialTime);
  const [running, setRunning] = useState(false);

  // Array de scores pour chaque reprise
  const [rounds, setRounds] = useState(Array(roundsCount).fill().map(() => ({
    scoreRed: { poings: 0, pieds: 0 },
    scoreBlue: { poings: 0, pieds: 0 },
    sanctionsRed: 0,
    sanctionsBlue: 0,
    endedBySanction: false,
  })));
  const timerRef = useRef();
  const audioRef = useRef();

  // Ensure rounds array length tracks possible nbReprises change
  useEffect(() => {
    setRounds(rs => {
      const wanted = roundsCount;
      if (rs.length === wanted) return rs;
      const copy = rs.slice(0, wanted);
      while (copy.length < wanted) {
        copy.push({
          scoreRed: { poings: 0, pieds: 0 },
          scoreBlue: { poings: 0, pieds: 0 },
          sanctionsRed: 0,
          sanctionsBlue: 0,
          endedBySanction: false,
        });
      }
      return copy;
    });
    // reset timer when params change
    setTime(initialTime);
    setCurrentRound(1);
    setRunning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundsCount, initialTime]);

  // Gestion du timer
  useEffect(() => {
    if (running && time > 0) {
      timerRef.current = setInterval(() => setTime(t => t - 1), 1000);
    }
    if ((!running || time <= 0) && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (time === 0 && running) {
      setRunning(false);
      clearInterval(timerRef.current);
      if (audioRef.current) audioRef.current.play().catch(() => {});
      setTimeout(() => nextRoundOrFinish(), 700);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, time]);

  // Fin du round ou match -> construit l'objet match complet envoyé à onFinish
  function buildMatchResult(finalRounds) {
    const participants = {
      blueId: params.blueId || null,
      redId: params.redId || null,
      arbitreId: params.arbitreId || null,
      nomBlue: params.nomBlue || params.blueName || null,
      nomRed: params.nomRed || params.redName || null,
    };
    return {
      params: {
        type: params.type,
        durationMin: params.durationMin,
        durationSec: params.durationSec,
        valeurPoing: params.valeurPoing,
        valeurPied: params.valeurPied,
        nbReprises: roundsCount,
        classeId: params.classeId || null,
        activity: params.activity || params.activityName || "Boxe Française",
        // keep any other settings
        ...params
      },
      rounds: finalRounds,
      participants,
      createdAt: new Date().toISOString()
    };
  }

  function nextRoundOrFinish() {
    if (currentRound < roundsCount) {
      setCurrentRound(c => c + 1);
      setTime(initialTime);
      setRunning(false);
    } else {
      const result = buildMatchResult(rounds);
      if (typeof onFinish === "function") onFinish(result);
    }
  }

  // Gestion des sanctions (DISQUALIFICATION)
  function handleSanction(color, value) {
    setRounds(prevRounds => {
      const idx = currentRound - 1;
      const round = prevRounds[idx];
      if (!round) return prevRounds;
      let sanctions = color === "red" ? round.sanctionsRed : round.sanctionsBlue;
      sanctions = Math.max(0, Math.min(3, sanctions + value));
      const newRound = { ...round, sanctionsRed: round.sanctionsRed, sanctionsBlue: round.sanctionsBlue };
      if (color === "red") newRound.sanctionsRed = sanctions;
      else newRound.sanctionsBlue = sanctions;

      // If disqualification -> mark endedBySanction and finish match immediately
      if (sanctions === 3) {
        newRound.endedBySanction = true;
        const newRounds = prevRounds.map((r, i) => i === idx ? newRound : r);
        // stop timer
        setRunning(false);
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        // small timeout to allow UI update before navigating/saving
        setTimeout(() => {
          const result = buildMatchResult(newRounds);
          if (typeof onFinish === "function") onFinish(result);
        }, 700);
        return newRounds;
      }

      return prevRounds.map((r, i) => i === idx ? newRound : r);
    });
  }

  // Gestion score
  function handleScore(color, type, value = 1) {
    const idx = currentRound - 1;
    setRounds(rs => rs.map((r, i) => {
      if (i !== idx) return r;
      const key = color === "red" ? "scoreRed" : "scoreBlue";
      const newScore = { ...r[key] };
      newScore[type] = Math.max(0, (newScore[type] || 0) + value);
      return { ...r, [key]: newScore };
    }));
  }

  // Reset round
  function resetCurrentRound() {
    setRounds(rs => rs.map((r, idx) =>
      idx === currentRound - 1 ? {
        scoreRed: { poings: 0, pieds: 0 },
        scoreBlue: { poings: 0, pieds: 0 },
        sanctionsRed: 0,
        sanctionsBlue: 0,
        endedBySanction: false,
      } : r
    ));
    setTime(initialTime);
    setRunning(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }

  // Helpers for display names (prefer provided manual names, then ids)
  function displayBlue() {
    return params.nomBlue || params.blueName || params.blueId || "Bleu";
  }
  function displayRed() {
    return params.nomRed || params.redName || params.redId || "Rouge";
  }
  function displayArbitre() {
    return params.arbitreName || params.arbitreId || "—";
  }

  // Menu bas
  const menuBas = (
    <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
      <IconButton onClick={onHome}><HomeIcon /></IconButton>
      <IconButton onClick={() => { resetCurrentRound(); if (typeof onReset === "function") onReset(); }}><RestoreIcon /></IconButton>
      <IconButton><TimerIcon /></IconButton>
    </Box>
  );

  // Infos round en cours
  const round = rounds[currentRound - 1];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
      <audio src="/assets/bip.mp3" ref={audioRef} preload="auto" />
      <Paper sx={{ mt: 2, p: { xs: 1, sm: 3 }, minWidth: 320, width: "95%", maxWidth: 620 }}>
        <Typography variant="h6" align="center" sx={{ mb: 1 }}>
          Reprise {currentRound} / {roundsCount}
        </Typography>

        <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <img src="/assets/gantrouge.png" alt="gant rouge" style={{ width: 48, margin: "0 4px" }} />
            <Typography sx={{ mt: 1, fontWeight: 600 }}>{displayRed()}</Typography>
          </Grid>

          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <TimerIcon sx={{ fontSize: 48 }} />
            <Typography sx={{ fontSize: 32 }}>{`${Math.floor(time / 60)}' ${(time % 60).toString().padStart(2, "0")}"`}</Typography>
            <Button variant="contained" onClick={() => setRunning(r => !r)}>
              {running ? "Pause" : "Start"}
            </Button>
            {/* Affichage de l'arbitre sous le timer (utilise displayArbitre pour éviter le warning ESLint) */}
            <Typography sx={{ mt: 1, fontSize: 14, color: "#555" }}>{displayArbitre()}</Typography>
          </Grid>

          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <img src="/assets/gantbleu.png" alt="gant bleu" style={{ width: 48, margin: "0 4px" }} />
            <Typography sx={{ mt: 1, fontWeight: 600 }}>{displayBlue()}</Typography>
          </Grid>
        </Grid>

        {/* Scores et sanctions pour chaque boxeur */}
        <Grid container spacing={1} justifyContent="center">
          {/* Rouge */}
          <Grid item xs={6}>
            <Box sx={{ bgcolor: "#ffcaca", borderRadius: 2, p: 1 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Button onClick={() => handleScore("red", "poings", params.valeurPoing ?? 1)}>
                  <img src="/assets/gantrouge.png" alt="Poing Rouge" style={{ width: 42 }} />
                </Button>
                <span style={{ fontSize: 20 }}>{round?.scoreRed?.poings ?? 0}</span>
                {params.type === "piedspoings" && (
                  <>
                    <Button onClick={() => handleScore("red", "pieds", params.valeurPied ?? 2)}>
                      <img src="/assets/whiteshoes.png" alt="Pied Rouge" style={{ width: 38, filter: "drop-shadow(0 0 4px red)" }} />
                    </Button>
                    <span style={{ fontSize: 20 }}>{round?.scoreRed?.pieds ?? 0}</span>
                  </>
                )}
              </div>
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <Typography variant="subtitle2" sx={{ color: "red", fontWeight: "bold", mb: 0.5 }}>
                  SANCTIONS
                </Typography>
                <Button onClick={() => handleSanction("red", -1)}>-</Button>
                <span style={{ color: "red", fontWeight: 600 }}>{round?.sanctionsRed ?? 0}</span>
                <Button onClick={() => handleSanction("red", 1)}>+</Button>
                <Typography sx={{ color: (round?.sanctionsRed === 3 ? "red" : "#222"), fontWeight: "bold", mt: 1 }}>
                  {sanctionsLabels[round?.sanctionsRed ?? 0]}
                </Typography>
              </div>
            </Box>
          </Grid>

          {/* Bleu */}
          <Grid item xs={6}>
            <Box sx={{ bgcolor: "#c9e0ff", borderRadius: 2, p: 1 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Button onClick={() => handleScore("blue", "poings", params.valeurPoing ?? 1)}>
                  <img src="/assets/gantbleu.png" alt="Poing Bleu" style={{ width: 42 }} />
                </Button>
                <span style={{ fontSize: 20 }}>{round?.scoreBlue?.poings ?? 0}</span>
                {params.type === "piedspoings" && (
                  <>
                    <Button onClick={() => handleScore("blue", "pieds", params.valeurPied ?? 2)}>
                      <img src="/assets/whiteshoes.png" alt="Pied Bleu" style={{ width: 38, filter: "drop-shadow(0 0 4px blue)" }} />
                    </Button>
                    <span style={{ fontSize: 20 }}>{round?.scoreBlue?.pieds ?? 0}</span>
                  </>
                )}
              </div>
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <Typography variant="subtitle2" sx={{ color: "blue", fontWeight: "bold", mb: 0.5 }}>
                  SANCTIONS
                </Typography>
                <Button onClick={() => handleSanction("blue", -1)}>-</Button>
                <span style={{ color: "blue", fontWeight: 600 }}>{round?.sanctionsBlue ?? 0}</span>
                <Button onClick={() => handleSanction("blue", 1)}>+</Button>
                <Typography sx={{ color: (round?.sanctionsBlue === 3 ? "red" : "#222"), fontWeight: "bold", mt: 1 }}>
                  {sanctionsLabels[round?.sanctionsBlue ?? 0]}
                </Typography>
              </div>
            </Box>
          </Grid>
        </Grid>

        {menuBas}

        {/* Navigation entre reprises si plusieurs */}
        {roundsCount > 1 && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              disabled={currentRound === 1}
              onClick={() => { setCurrentRound(c => c - 1); setTime(initialTime); setRunning(false); }}
            >Reprise précédente</Button>
            <Button
              disabled={currentRound === roundsCount}
              onClick={() => { setCurrentRound(c => c + 1); setTime(initialTime); setRunning(false); }}
            >Reprise suivante</Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}